import { getAuth } from 'firebase/auth'
import { debug } from '@helpers/debug'
import { collectionGroup, query, where, orderBy, limit, getFirestore, getDocs } from 'firebase/firestore'
import { logger } from '@helpers/logger'
import { colors } from '@helpers/logger'
import { getUserTournamentEntry } from './userTournamentEntryService'

/**
 * Retrieves up to 100 entrant documents where `uid` matches the current user's ID and `eventsRemaining` is greater than 0,
 * ordered by `eventsRemaining` in descending order. First loads entrants to identify joined tournaments, then processes
 * featured tournaments (marking those that are joined), and finally processes remaining entrants.
 * @returns {Promise<Array>} - A promise that resolves to an array of tournament pool documents.
 */
export const getActiveTournamentPools = async (featuredTournamentPoolsSummary, setActiveTournamentPoolData, setHasActiveTournaments) => {
  const performanceLogs = []
  const isLoggingEnabled = debug.performanceLoggingEnabled === true
  const params = {}
  const logComplete = logger.serviceRequest('getActiveTournamentPools', params)

  // Track tournament keys that have already been processed to prevent duplicate calls
  const processedTournamentKeys = new Set()
  // Store tournament keys the user has joined (from entrants collection)
  const joinedTournamentKeys = new Set()
  // Store promises for tournament entry requests to ensure we only call once per key
  const tournamentEntryPromises = new Map()

  try {
    const auth = getAuth()
    const currentUser = auth.currentUser

    if (!currentUser) {
      throw new Error('No authenticated user found.')
    }

    const userKey = currentUser.uid

    const db = getFirestore()

    // Helper function to get tournament entry (only once per key)
    const getAndProcessTournamentEntry = async (tournamentKey) => {
      // Skip if already processed
      if (processedTournamentKeys.has(tournamentKey)) {
        return
      }

      // Mark as processed
      processedTournamentKeys.add(tournamentKey)

      // Use existing promise if one exists for this tournament key
      if (!tournamentEntryPromises.has(tournamentKey)) {
        const startTime = Date.now()
        logger.logWithTime('activeTournamentPoolsLoading', 'getActiveTournamentPools.js', `Fetching tournament entry for key: ${tournamentKey}`)
        tournamentEntryPromises.set(
          tournamentKey,
          getUserTournamentEntry(tournamentKey).then((entry) => {
            const duration = Date.now() - startTime
            logger.logWithTime('activeTournamentPoolsLoading', 'getActiveTournamentPools.js', `Tournament entry fetched for key: ${tournamentKey}`, {
              duration: `${duration}ms`,
              eventsCount: entry?.userEntry?.userTournamentPoolEntryEvents?.length || 0,
              tournamentKey: tournamentKey,
            })
            return entry
          }),
        )
      }

      const userTournamentEntry = await tournamentEntryPromises.get(tournamentKey)
      if (userTournamentEntry) {
        // Set joined flag if this tournament exists in the joinedTournamentKeys set
        if (joinedTournamentKeys.has(tournamentKey)) {
          userTournamentEntry.joined = true
        }
        setActiveTournamentPoolData(userTournamentEntry)
      }
    }

    // First, query entrants to identify which tournaments the user has joined
    const entrantsQuery = query(
      collectionGroup(db, 'entrants'),
      where('uid', '==', userKey),
      where('eventsRemaining', '>', 0),
      orderBy('eventsRemaining', 'desc'),
      orderBy('createdUtcTimeStamp', 'desc'),
      limit(100),
    )

    const entrantsSnapshot = await getDocs(entrantsQuery)

    // Build a set of tournament keys the user has joined
    if (entrantsSnapshot.empty) {
      setHasActiveTournaments(false)
      setActiveTournamentPoolData([])
      return // Exit early if no tournaments
    } else {
      setHasActiveTournaments(true)

      // Process all entrants in parallel
      const entrantPromises = entrantsSnapshot.docs.map(async (entrantDoc) => {
        const tournamentKey = entrantDoc.ref.parent.parent.id
        if (!tournamentKey) {
          logger.error('getActiveTournamentPools', 'Tournament key is undefined for entrant document:', entrantDoc)
          return null
        }
        joinedTournamentKeys.add(tournamentKey)
        const startTime = Date.now()
        logger.logWithTime('activeTournamentPoolsLoading', 'getActiveTournamentPools.js', `Fetching tournament entry for tournamentKey: ${tournamentKey}`)
        const userTournamentEntry = await getUserTournamentEntry(tournamentKey)
        const duration = Date.now() - startTime
        logger.logWithTime('activeTournamentPoolsLoading', 'getActiveTournamentPools.js', `Tournament entry fetched for tournamentKey: ${tournamentKey}`, {
          duration: `${duration}ms`,
          eventsCount: userTournamentEntry?.userEntry?.userTournamentPoolEntryEvents?.length || 0,
          tournamentKey: tournamentKey,
        })
        if (userTournamentEntry) {
          logger.logWithTime(
            'activeTournamentPoolsLoading',
            'getActiveTournamentPools.js',
            `Joined Tournament entry fetched for tournamentKey: ${tournamentKey}`,
            undefined,
            colors.yellow,
          )
          userTournamentEntry.joined = true
          return userTournamentEntry
        }
        return null
      })

      // Wait for all promises to resolve and filter out null entries
      const results = await Promise.all(entrantPromises)
      const validEntries = results.filter((entry) => entry !== null)

      logger.logWithTime('activeTournamentPoolsLoading', 'getActiveTournamentPools.js', `setActiveTournamentPoolData`, undefined, colors.red)
      setActiveTournamentPoolData(validEntries)
    }

    // Next, load featured tournament pools (marking those that are joined)
    if (featuredTournamentPoolsSummary && featuredTournamentPoolsSummary.length > 0) {
      // Create an array of promises for featured tournaments
      const featuredPromises = featuredTournamentPoolsSummary.map((poolSummary) => {
        return getAndProcessTournamentEntry(poolSummary.tournamentKey)
      })

      // Process featured tournaments in parallel
      await Promise.all(featuredPromises)
    }

    // Finally, process all entrants to ensure we load any non-featured tournaments the user has joined
    if (!entrantsSnapshot.empty) {
      // Create an array of promises for entrant tournaments that haven't been processed yet
      const entrantPromises = entrantsSnapshot.docs
        .map((entrantDoc) => {
          const tournamentKey = entrantDoc.ref.parent.parent.id
          // Only process if not already processed (e.g., as a featured tournament)
          if (!processedTournamentKeys.has(tournamentKey)) {
            return getAndProcessTournamentEntry(tournamentKey)
          }
          return null
        })
        .filter(Boolean) // Remove null entries

      // Process remaining entrant tournaments in parallel
      await Promise.all(entrantPromises)
    }

    logComplete()
  } catch (error) {
    logger.error('getActiveTournamentPools', error)
    if (isLoggingEnabled) {
      logger.log('Performance Logs:', performanceLogs)
    }
    logComplete()
  }
}
